import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
/**
 * Glyph — renders one icon from the PAN iconography library (branding
 * plan B3b), INLINE so its `currentColor` fills inherit the
 * surrounding text/accent color (an <img> can't do that — the whole
 * reason the extractor emitted currentColor SVGs).
 *
 * Fetches the SVG text once per URL (module-level cache shared across
 * every instance) from the public branding bucket, so a category glyph
 * used in twenty chips costs one request. Renders an empty fixed-size
 * span while loading — no layout shift — and stays empty on failure
 * (icons are decoration; nothing breaks without them).
 */
const cache = new Map();
/**
 * Strip active content from fetched SVG before it is injected via
 * dangerouslySetInnerHTML (FE-4/FE-5). The primary control is still that `url`
 * points at the trusted branding bucket; this is defense-in-depth so a
 * script-bearing SVG can't execute if that trust is ever violated. Kept to a
 * dependency-free regex pass to respect the shared package's bundle-weight
 * budget (GAS consumes @techstack/ui); for fully-untrusted SVG use DOMPurify.
 */
function sanitizeSvg(svg) {
    if (!svg || !svg.includes('<svg'))
        return '';
    return svg
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, '')
        // inline event handlers: on…="…" / on…='…' / on…=bareword
        .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '')
        .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '')
        .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
        // neutralize javascript: in href / xlink:href
        .replace(/(href|xlink:href)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1=$2#$2');
}
function loadSvg(url) {
    const hit = cache.get(url);
    if (hit !== undefined)
        return hit;
    const p = fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((text) => {
        const svg = text.includes('<svg') ? sanitizeSvg(text) : '';
        cache.set(url, svg);
        return svg;
    })
        .catch(() => {
        cache.set(url, '');
        return '';
    });
    cache.set(url, p);
    return p;
}
export function Glyph({ url, size = 18, label }) {
    const initial = cache.get(url);
    const [svg, setSvg] = React.useState(typeof initial === 'string' ? initial : '');
    React.useEffect(() => {
        let live = true;
        const res = loadSvg(url);
        if (typeof res === 'string')
            setSvg(res);
        else
            res.then((s) => { if (live)
                setSvg(s); });
        return () => { live = false; };
    }, [url]);
    return (_jsx("span", { className: "glyph", style: { width: size, height: size }, role: label ? 'img' : undefined, "aria-label": label, "aria-hidden": label ? undefined : true, dangerouslySetInnerHTML: { __html: svg } }));
}
